import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clearLegalHash, legalKindFromDocUrl, setLegalHash } from "@Components/legal/legalHash";

interface LegalDrawerState {
  url: string;
  title: string;
}

interface LegalDrawerContextValue {
  isOpen: boolean;
  url: string;
  title: string;
  open: (url: string, title: string) => void;
  close: () => void;
}

const LegalDrawerContext = createContext<LegalDrawerContextValue>({
  isOpen: false,
  url: "",
  title: "",
  open: () => undefined,
  close: () => undefined
});

export const LegalDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<LegalDrawerState>({ url: "", title: "" });

  const open = useCallback((url: string, title: string) => {
    setState({ url, title });
    setIsOpen(true);
    setLegalHash(legalKindFromDocUrl(url));
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    clearLegalHash();
  }, []);

  const value = useMemo(
    () => ({ isOpen, url: state.url, title: state.title, open, close }),
    [isOpen, state.url, state.title, open, close]
  );
  return <LegalDrawerContext.Provider value={value}>{children}</LegalDrawerContext.Provider>;
};

export const useLegalDrawer = (): LegalDrawerContextValue => useContext(LegalDrawerContext);

// Helper: does this href point at a static legal document served from
// wwwroot (e.g. /legal/subscription-license-agreement.pt.md)? Footer
// link handlers use this to open the in-page drawer instead of routing.
export const isLegalDocHref = (href?: string | null): boolean => {
  if (!href) return false;
  return href.startsWith("/legal/") || /\.md$/i.test(href);
};
